import {I18n} from '@aws-amplify/core';
import {DataTableColumn} from 'mantine-datatable';
import {NavLink} from 'react-router-dom';

import {Product} from '../types';

// FNP-918: was `tables/columns.tsx` — a bare `getColumns()` array where EVERY
// column was `render`-only, with no `accessor` on any of them.
//
// That is the distinction that made this module unsortable. `render` controls
// how a cell LOOKS; `accessor` is what sorting sorts BY. A render-only config
// looks complete in review and makes sorting structurally impossible — you
// cannot add `sortable: true` to a column that has nothing to sort on. Moved
// into a hook to match the platform shape (checklist §3z).
//
// ⚠️ TWO COLUMNS ARE GONE, deliberately: Link (`url`) and Created.
//
// `listProducts` selects exactly `id` and `name` — open services/queries.ts and
// that is the entire field list, on both the list and the get query. So `url`
// rendered as blank on every row and `created` rendered as an em-dash on every
// row. Neither was a data problem; both were fields nobody had ever fetched.
//
// A blank Link column is the worse of the two: it renders as genuinely EMPTY
// rather than as a placeholder, so it reads as "this product has no URL"
// instead of "nobody asked the API for one".
//
// Bringing either back means adding the field to the GraphQL selection set —
// a change to what this module asks the API for, which is a decision about the
// query, not a column config. Removing the dead columns is the honest half.
//
// That leaves TWO columns, which is what this entity actually is today. A
// two-column table is not a gap; it is an accurate rendering of a two-field
// record.
export const useProductsTableColumns = () => {

    const columns: DataTableColumn<Product>[] = [
        {
            accessor: 'name',
            sortable: true,
            title: I18n.get('Name'),
            width: '1fr',
            // A real NavLink, always. The old column rendered a <span> with an
            // inline blue colour and a cursor style faking a link whenever an
            // `onRecordClick` was passed — not keyboard-reachable, no focus
            // ring (§2 no hardcoded colours, §13 accessibility).
            //
            // `name` is nullable on the type; falls back to the id so the row
            // is never a blank link.
            render: (item: Product) => (
                <NavLink to={`/products/${encodeURIComponent(item.id)}`}>
                    {item.name || item.id}
                </NavLink>
            ),
        },
        {
            accessor: 'id',
            sortable: true,
            title: I18n.get('Product ID'),
            width: '1fr',
            // The Stripe id (`prod_...`) is what an operator matches against
            // the Stripe dashboard, and it is one of only two fields this API
            // returns — so it earns a column rather than hiding behind the
            // name.
            render: (item: Product) => item.id,
        },
    ];

    return {columns};
};
